import React, { useEffect, useState, useCallback, useRef } from 'react';
import './AddProjectPage.css';
import ProjectCardCompact from "./ProjectCardCompact";

interface ProjectCategory {
  id: number;
  name: string;
  description: string;
  icon: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface AvailableProject {
  id: number;
  name: string;
  category: string;
  description: string;
  icon: string;
  owner_type: string;
  is_selected: boolean;
  percentage: number;
  hours: number;
  target_hours: number;
  change_percentage: number;
  change_type: string;
  start_date?: string;
  end_date?: string;
}

interface AddProjectPageProps {
  authenticatedFetch?: (url: string, options?: RequestInit) => Promise<Response>;
  onProjectAdded: () => void;
  onBack: () => void;
}

const AddProjectPage: React.FC<AddProjectPageProps> = ({ 
  authenticatedFetch, 
  onProjectAdded, 
  onBack 
}) => {
  const [categories, setCategories] = useState<ProjectCategory[]>([]);
  const [projects, setProjects] = useState<AvailableProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showTimeForm, setShowTimeForm] = useState(false);
  const [selectedProject, setSelectedProject] = useState<AvailableProject | null>(null);
  const [message, setMessage] = useState('');
  
  // 添加每周预算时间状态
  const [weeklyBudgetHours, setWeeklyBudgetHours] = useState<string>('');
  
  // 自定义项目相关状态
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customProjectName, setCustomProjectName] = useState('');
  const [customProjectDesc, setCustomProjectDesc] = useState('');
  const [customProjectIcon, setCustomProjectIcon] = useState('📁');
  
  const fetchingCategoriesRef = useRef(false); // 防止重复请求分类
  const fetchingProjectsRef = useRef(false); // 防止重复请求项目

  // 获取分类列表
  const fetchCategories = useCallback(async () => {
    // 防止重复请求
    if (fetchingCategoriesRef.current) {
      return;
    }
    fetchingCategoriesRef.current = true;
    
    try {
      const fetchFunction = authenticatedFetch || fetch;
      const res = await fetchFunction('/api/categories');
      const data = await res.json();
      if (data.success) {
        setCategories(data.data);
      }
    } catch (error) {
      console.error('获取分类列表失败:', error);
    } finally {
      fetchingCategoriesRef.current = false;
    }
  }, [authenticatedFetch]);

  // 获取可用项目列表
  const fetchAvailableProjects = useCallback(async () => {
    // 防止重复请求
    if (fetchingProjectsRef.current) {
      return;
    }
    fetchingProjectsRef.current = true;
    
    setLoading(true);
    try {
      const fetchFunction = authenticatedFetch || fetch;
      const res = await fetchFunction('/api/me/projects/available');
      const data = await res.json();
      setProjects(data);
    } catch (error) {
      console.error('获取项目列表失败:', error);
    } finally {
      setLoading(false);
      fetchingProjectsRef.current = false;
    }
  }, [authenticatedFetch]);

  // 删除 getCurrentLocation 和 handleManualLocationRequest

  useEffect(() => {
    fetchCategories();
    fetchAvailableProjects();
    // 设置默认每周预算时间为0
    setWeeklyBudgetHours('');
    // 删除自动获取地理位置的调用
  }, [authenticatedFetch]); // 直接依赖 authenticatedFetch，避免函数重新创建导致的重复执行

  // 根据选中的分类过滤项目，并排除已选中的项目
  const getFilteredProjects = () => {
    // 首先过滤掉已选中的项目
    const unselectedProjects = projects.filter(project => !project.is_selected);
    
    if (selectedCategory === 'all') {
      return unselectedProjects.filter(project => project.owner_type === 'system');
    } else {
      const category = categories.find(cat => cat.id.toString() === selectedCategory);
      if (category && category.name === '自定义') {
        // 自定义分类显示用户创建的项目
        return unselectedProjects.filter(project => project.owner_type === 'user');
      } else {
        // 其他分类显示系统项目
        return unselectedProjects.filter(project => 
          category && project.category === category.name && project.owner_type === 'system'
        );
      }
    }
  };

  const filteredProjects = getFilteredProjects();

  // 处理项目点击
  const handleProjectClick = (project: AvailableProject) => {
    setSelectedProject(project);
    setWeeklyBudgetHours(''); // 重置每周预算时间
    setShowTimeForm(true);
  };

  // 添加项目
  const handleAddProject = async () => {
    if (!selectedProject) return;
    
    const hours = parseFloat(weeklyBudgetHours) || 0;
    if (hours === 0) {
      setMessage('每周预算时间不能为0，请重新设置。');
      return;
    }

    // 设置开始时间为当前时间（东八区）
    const now = new Date();
    const utc8Time = new Date(now.getTime() + (8 * 60 * 60 * 1000));
    const currentStartDate = utc8Time.toISOString().slice(0, 16);

    try {
      const fetchFunction = authenticatedFetch || fetch;
      const res = await fetchFunction('/api/me/add_project', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          project_id: selectedProject.id,
          percentage: 0,
          hours: 0,
          target_hours: hours,
          change_percentage: 0,
          change_type: 'stable',
          start_date: currentStartDate,
          end_date: null, // 由于删除了"一直持续"选项，默认设置为null表示一直持续
          is_active: true
        })
      });

      if (res.ok) {
        setMessage('项目添加成功！');
        setShowTimeForm(false);
        setSelectedProject(null);
        setWeeklyBudgetHours(''); // 重置每周预算时间
        onProjectAdded();
        // 延迟返回首页
        setTimeout(() => {
          onBack();
        }, 1500);
      } else {
        const errorData = await res.json();
        setMessage(`添加失败：${errorData.detail || '请重试'}`);
      }
    } catch (error) {
      console.error('添加项目失败:', error);
      setMessage('网络错误，请重试');
    }
  };

  // 删除自定义项目
  const handleDeleteCustomProject = async () => {
    if (!selectedProject) return;
    
    if (!window.confirm('确定要删除这个自定义项目吗？此操作不可撤销。')) {
      return;
    }
    
    try {
      const fetchFunction = authenticatedFetch || fetch;
      const res = await fetchFunction(`/api/me/projects/custom/${selectedProject.id}`, {
        method: 'DELETE',
      });

      if (res.ok) {
        setMessage('自定义项目删除成功！');
        setShowTimeForm(false);
        setSelectedProject(null);
        setWeeklyBudgetHours(''); // 重置每周预算时间
        onProjectAdded();
        // 延迟返回首页
        setTimeout(() => {
          onBack();
        }, 1500);
      } else {
        const errorData = await res.json();
        setMessage(`删除失败：${errorData.detail || '请重试'}`);
      }
    } catch (error) {
      console.error('删除自定义项目失败:', error);
      setMessage('网络错误，请重试');
    }
  };

  // 创建自定义项目
  const handleCreateCustomProject = async () => {
    if (!customProjectName.trim()) {
      setMessage('请输入项目名称');
      return;
    }

    const hours = parseFloat(weeklyBudgetHours) || 0;
    if (hours === 0) {
      setMessage('每周预算时间不能为0，请重新设置。');
      return;
    }

    // 设置开始时间为当前时间（东八区）
    const now = new Date();
    const utc8Time = new Date(now.getTime() + (8 * 60 * 60 * 1000));
    const currentStartDate = utc8Time.toISOString().slice(0, 16);

    try {
      const fetchFunction = authenticatedFetch || fetch;
      const res = await fetchFunction('/api/me/projects/custom', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: customProjectName.trim(),
          description: customProjectDesc.trim(),
          icon: customProjectIcon,
          category: '自定义',
          target_hours: hours, // 设置每周预算时间
          start_date: currentStartDate,
          end_date: null, // 默认设置为null表示一直持续
          is_active: true
        })
      });

      if (res.ok) {
        setMessage('自定义项目创建成功！');
        setShowCustomForm(false);
        setCustomProjectName('');
        setCustomProjectDesc('');
        setCustomProjectIcon('📁');
        setWeeklyBudgetHours(''); // 重置每周预算时间
        onProjectAdded();
        // 延迟返回首页
        setTimeout(() => {
          onBack();
        }, 1500);
      } else {
        const errorData = await res.json();
        setMessage(`创建失败：${errorData.detail || '请重试'}`);
      }
    } catch (error) {
      console.error('创建自定义项目失败:', error);
      setMessage('网络错误，请重试');
    }
  };

  if (showTimeForm) {
    return (
      <div className="add-project-page">
        
        <div className="time-form-container">
          <div className="selected-project-info">
            <div className="project-icon">{selectedProject?.icon}</div>
            <div className="project-info">
              <h3>{selectedProject?.name}</h3>
              <p>{selectedProject?.description}</p>
            </div>
          </div>
          
          <div className="form-group">
            <label>项目每周预算时间（小时）：</label>
            <input
              type="text"
              value={weeklyBudgetHours}
              onChange={(e) => setWeeklyBudgetHours(e.target.value)}
              placeholder="请输入每周预算时间"
              className="budget-hours-input"
            />
            <small className="form-help-text">设置每周计划投入该项目的时间（小时）</small>
          </div>
          
          <div className="form-actions">
            <button onClick={handleAddProject} className="add-btn">
              添加项目
            </button>
            {selectedProject?.owner_type === 'user' && (
              <button onClick={handleDeleteCustomProject} className="delete-btn">
                删除项目
              </button>
            )}
            <button onClick={() => {
              setShowTimeForm(false);
              setWeeklyBudgetHours(''); // 重置每周预算时间
            }} className="cancel-btn">
              取消
            </button>
          </div>
        </div>
        
        {message && (
          <>
            <div className="message-overlay" onClick={() => setMessage('')}></div>
            <div className="message">{message}</div>
          </>
        )}
      </div>
    );
  }

  if (showCustomForm) {
    return (
      <div className="add-project-page">
        
        <div className="custom-form-container">
          <div className="form-group">
            <label>项目名称：</label>
            <input
              type="text"
              value={customProjectName}
              onChange={(e) => setCustomProjectName(e.target.value)}
              placeholder="请输入项目名称"
              required
            />
          </div>
          
          <div className="form-group">
            <label>项目描述：</label>
            <textarea
              value={customProjectDesc}
              onChange={(e) => setCustomProjectDesc(e.target.value)}
              placeholder="请输入项目描述（可选）"
            />
          </div>
          
          <div className="form-group">
            <label>项目图标：</label>
            <div className="icon-selector">
              {[
                '📁', '🎯', '💡', '⭐', '🔥', '🚀', '💎',
                '🌟', '🎨', '📚', '💻', '🎵', '🏃', '🍳',
                '✈️', '🏠', '💼', '🎭', '📷', '🎮', '🏖️',
                '🌱', '🔬', '🎪', '🏛️', '🎡', '🗽', '🎯'
              ].map(icon => (
                <button
                  key={icon}
                  className={`icon-option ${customProjectIcon === icon ? 'selected' : ''}`}
                  onClick={() => setCustomProjectIcon(icon)}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>
          
          <div className="form-group">
            <label>项目每周预算时间（小时）：</label>
            <input
              type="text"
              value={weeklyBudgetHours}
              onChange={(e) => setWeeklyBudgetHours(e.target.value)}
              placeholder="请输入每周预算时间"
              className="budget-hours-input"
            />
            <small className="form-help-text">设置每周计划投入该项目的时间（小时）</small>
          </div>
          
          <div className="form-actions">
            <button onClick={handleCreateCustomProject} className="add-btn">
              创建项目
            </button>
            <button onClick={() => {
              setShowCustomForm(false);
              setCustomProjectName('');
              setCustomProjectDesc('');
              setCustomProjectIcon('📁');
              setWeeklyBudgetHours(''); // 重置每周预算时间
            }} className="cancel-btn">
              取消
            </button>
          </div>
        </div>
        
        {message && (
          <>
            <div className="message-overlay" onClick={() => setMessage('')}></div>
            <div className="message">{message}</div>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="add-project-layout">
        <aside className="sidebar">
          <div className="category-list">
            {/* 分类菜单项 */}
            <div
              className={`category-item${selectedCategory === 'all' ? ' active' : ''}`}
              onClick={() => setSelectedCategory('all')}
            >
              全部项目
            </div>
            {categories.map(cat => (
              <div
                key={cat.id}
                className={`category-item${selectedCategory === cat.id.toString() ? ' active' : ''}`}
                onClick={() => setSelectedCategory(cat.id.toString())}
              >
                {cat.name}
              </div>
            ))}
          </div>
        </aside>
        <main className="project-main">
          <div className="project-list-compact">
            {filteredProjects.map(project => (
              <ProjectCardCompact
                key={project.id}
                icon={project.icon}
                name={project.name}
                onClick={() => handleProjectClick(project)}
              />
            ))}
            
            {/* 虚线框的+按钮 - 紧挨着项目列表 */}
            {categories.find(cat => cat.id.toString() === selectedCategory)?.name === '自定义' && (
              <div className="custom-add-card" onClick={() => setShowCustomForm(true)}>
                <div className="custom-add-icon">+</div>
                <div className="custom-add-text">创建自定义项目</div>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default AddProjectPage; 